import {makeAutoObservable, observable} from 'mobx';

import {Boost, User} from '@/domain/models';

import {MapBounds, MapOnboardingSteps} from './types';

class Store {
  readonly boosts = observable.map<Boost['user_id'], Boost>();
  readonly users = observable.map<User['id'], User>();
  private _mapBounds: MapBounds | null = null;
  private _mapZoom: number = 3;
  private _myBoost?: Boost;
  private _fetched: boolean = false;
  private _mapOnboardingStep: MapOnboardingSteps =
    MapOnboardingSteps.simpleBoostOnboarding;

  constructor() {
    makeAutoObservable(this);
  }

  get fetched() {
    return this._fetched;
  }

  get myBoost() {
    return this._myBoost;
  }

  get mapOnboardingStep() {
    return this._mapOnboardingStep;
  }

  setMapOnboardingStep(step: MapOnboardingSteps) {
    this._mapOnboardingStep = step;
  }

  setMyBoost(boost?: Boost) {
    this._myBoost = boost;
  }

  get mapBounds() {
    return this._mapBounds;
  }

  setMapBounds(mapBounds: MapBounds | null): void {
    this._mapBounds = mapBounds;
  }

  get asBoostsArray(): Boost[] {
    return Array.from(this.boosts.values());
  }

  add(boost: Boost, user: User) {
    this.boosts.set(boost.user_id, boost);
    this.users.set(user.id, user);
  }

  addMultiple({boosts, users}: {boosts: Boost[]; users: User[]}) {
    this.boosts.merge(boosts.map((boost) => [boost.user_id, boost]));
    this.users.merge(users.map((user) => [user.id, user]));
    this._fetched = true;
  }

  resetBoosts() {
    this.boosts.clear();
    this.users.clear();
    this._fetched = false;
  }

  removeBoostByUserId(id: User['id']): void {
    this.users.delete(id);
    this.boosts.delete(id);
  }

  removeBoostByBoostId(id: Boost['id']): void {
    const boost = Array.from(this.boosts.values()).find(
      (boost) => boost.id === id
    );
    if (!boost) {
      return;
    }

    this.removeBoostByUserId(boost.user_id);
  }

  get mapZoom() {
    return this._mapZoom;
  }

  setMapZoom(zoom: number){
    this._mapZoom = zoom;
  }

  getUserById(id: User['id']) {
    return this.users.get(id);
  }

  getBoostByUserId(id: User['id']) {
    return this.boosts.get(id);
  }
}

export default new Store();
